import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { PRIMARY_SLOTS, MONTHS_DE } from "../lib/slots";
import { daysInMonth, holidaysForYear, isWeekend, parseISODateLocal, weekdayShortDe, ymd } from "../lib/calendar";
import { fmtDateDE } from "../lib/finance";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { PlanningIcon } from "../lib/planningIcons";

const DAY_LABEL_W = 60;
const SLOT_CELL_W = 28;

/** Build a map of dateKey -> [titles] for user-defined "special days". */
export function buildSpecialDaysMap(specialDays) {
  const map = new Map();
  if (!Array.isArray(specialDays)) return map;
  const push = (key, title) => {
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(title);
  };
  for (const sd of specialDays) {
    if (!sd?.start_date) continue;
    const start = parseISODateLocal(sd.start_date);
    if (!start) continue;
    const title = (sd.title || "").trim() || "Besonderer Tag";
    const end = sd.end_date ? parseISODateLocal(sd.end_date) : start;
    if (!end || end < start) {
      push(ymd(start.getFullYear(), start.getMonth(), start.getDate()), title);
      continue;
    }
    const cur = new Date(start);
    while (cur <= end) {
      push(ymd(cur.getFullYear(), cur.getMonth(), cur.getDate()), title);
      cur.setDate(cur.getDate() + 1);
    }
  }
  return map;
}

/** Build occupancy map: dateKey -> Map<slot, project[]> */
export function buildOccupancy(projects) {
  const map = new Map();
  for (const p of projects) {
    if (p.status === "archived") continue;
    const start = parseISODateLocal(p.start_date);
    const end = parseISODateLocal(p.end_date);
    if (!start || !end || end < start) continue;
    const cur = new Date(start);
    while (cur <= end) {
      const key = ymd(cur.getFullYear(), cur.getMonth(), cur.getDate());
      if (!map.has(key)) map.set(key, new Map());
      const slotMap = map.get(key);
      if (!slotMap.has(p.slot)) slotMap.set(p.slot, []);
      slotMap.get(p.slot).push(p);
      cur.setDate(cur.getDate() + 1);
    }
  }
  return map;
}

/** Build milestones map: `${dateKey}|${slot}` -> { title, project } */
export function buildMilestones(projects) {
  const map = new Map();
  for (const p of projects) {
    if (p.status === "archived") continue;
    if (!p.milestone_date) continue;
    if (p.milestone_date < p.start_date || p.milestone_date > p.end_date) continue;
    map.set(`${p.milestone_date}|${p.slot}`, { title: p.milestone_title || "Meilenstein", project: p });
  }
  return map;
}

/** 12 months rolling forward from anchor (year, month). */
function rollingMonths(startYear, startMonth) {
  const out = [];
  let y = startYear, m = startMonth;
  for (let i = 0; i < 12; i++) {
    out.push({ year: y, month: m });
    m++;
    if (m > 11) { m = 0; y++; }
  }
  return out;
}

const MonthColumn = React.forwardRef(function MonthColumn(
  { year, month, slotList, occupancy, milestones, holidays, specialDays, labelOverlays, planningIcons, onCellClick, onMilestoneDrop, onPlanningNoteDrop, onProjectSelect, todayKey },
  ref,
) {
  const dCount = daysInMonth(year, month);
  const days = [];
  for (let d = 1; d <= dCount; d++) days.push(d);
  const colWidth = DAY_LABEL_W + slotList.length * SLOT_CELL_W;

  return (
    <div
      ref={ref}
      className="flex flex-col bg-white border border-slate-200 rounded-md overflow-hidden shrink-0"
      style={{ width: colWidth }}
      data-testid={`month-column-${year}-${month + 1}`}
    >
      {/* Month header */}
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-medium text-slate-900" style={{ fontFamily: "Manrope, ui-sans-serif" }}>
            {MONTHS_DE[month]}
          </span>
          <span className="text-[10px] tabular-nums text-slate-400">
            {String(month + 1).padStart(2, "0")} · {year}
          </span>
        </div>
      </div>

      {/* Slot header */}
      <div className="flex border-b border-slate-200 bg-slate-50/60">
        <div style={{ width: DAY_LABEL_W }} className="shrink-0 border-r border-slate-200" />
        {slotList.map((s) => (
          <div
            key={s.n}
            style={{ width: SLOT_CELL_W }}
            className="shrink-0 text-center text-[9px] tabular-nums py-1 border-r border-slate-100 last:border-r-0 text-slate-500"
            title={`Slot ${s.n}`}
            data-testid={`vp-slot-header-${year}-${month + 1}-${s.n}`}
          >
            {s.n}
          </div>
        ))}
      </div>

      {/* Day rows */}
      <div style={{ position: "relative" }}>
        {Array.isArray(labelOverlays) && labelOverlays.map((o, i) => (
          <div
            key={`lo-${o.projectId}-${o.startDay}-${i}`}
            className="pointer-events-none"
            style={{
              position: "absolute",
              left: DAY_LABEL_W + o.slotIdx * SLOT_CELL_W,
              top: (o.startDay - 1) * 28,
              width: SLOT_CELL_W,
              maxHeight: (o.endDay - o.startDay + 1) * 28,
              overflow: "hidden",
              zIndex: 2,
              display: "flex",
              justifyContent: "center",
              paddingTop: 2,
            }}
            data-testid={`vp-short-label-${o.projectId}-${year}-${month + 1}`}
          >
            <span
              style={{
                display: "inline-block",
                writingMode: "vertical-lr",
                textOrientation: "upright",
                fontSize: 11,
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: "0.02em",
                color: "#0f172a",
                background: "rgba(255,255,255,0.9)",
                padding: "3px 2px",
                borderRadius: 3,
                boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
              }}
            >
              {o.label}
            </span>
          </div>
        ))}
        {days.map((day) => {
          const dateKey = ymd(year, month, day);
          const dateObj = new Date(year, month, day);
          const wknd = isWeekend(dateObj);
          const holidayName = holidays.get(dateKey);
          const dayOcc = occupancy.get(dateKey);
          const isToday = dateKey === todayKey;
          const specialTitles = specialDays?.get(dateKey);
          const isSpecial = Array.isArray(specialTitles) && specialTitles.length > 0;
          const dayLabelTitle = [
            ...(holidayName ? [holidayName] : []),
            ...(isSpecial ? specialTitles : []),
            ...(isToday && !holidayName && !isSpecial ? ["Heute"] : []),
          ].join(" · ");

          return (
            <div
              key={day}
              className={`flex h-7 border-b border-slate-100 last:border-b-0 ${
                holidayName ? "bg-rose-50/40" : wknd ? "bg-slate-100/60" : "bg-white"
              }`}
              data-special-day={isSpecial ? "true" : undefined}
            >
              <div
                style={{ width: DAY_LABEL_W }}
                className={`shrink-0 border-r border-slate-200 px-2 flex items-center justify-between text-[10px] tabular-nums transition-colors ${
                  isToday
                    ? "bg-slate-900 text-white font-semibold"
                    : isSpecial
                    ? "bg-green-100/80 text-slate-700"
                    : holidayName
                    ? "text-rose-500"
                    : wknd
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
                title={dayLabelTitle}
                data-testid={isToday ? "today-day-label" : undefined}
              >
                <span>{String(day).padStart(2, "0")}</span>
                <span className="text-[9px]">{weekdayShortDe(dateObj)}</span>
              </div>
              {slotList.map((s) => {
                const cellTestId = `wall-planner-cell-${dateKey}-${s.n}`;
                const projs = dayOcc?.get(s.n);
                const milestone = milestones.get(`${dateKey}|${s.n}`);
                if (projs && projs.length > 0) {
                  const primary = projs[0];
                  const conflict = projs.length > 1;
                  const milestoneProjectId = milestone ? milestone.project.id : null;
                  const iconEntries = planningIcons?.get(`${dateKey}|${s.n}`) || [];
                  const dropHandlers = (onMilestoneDrop || onPlanningNoteDrop) ? {
                    onDragOver: (e) => {
                      const dt = e.dataTransfer;
                      if (!dt) return;
                      if (dt.types?.includes("application/x-milestone") ||
                          dt.types?.includes("application/x-planning-note")) {
                        e.preventDefault();
                        dt.dropEffect = "move";
                      }
                    },
                    onDrop: (e) => {
                      const noteId = e.dataTransfer.getData("application/x-planning-note");
                      if (noteId && onPlanningNoteDrop) {
                        const [pid, nidxStr] = noteId.split("|");
                        const nidx = parseInt(nidxStr, 10);
                        if (pid && Number.isFinite(nidx)) {
                          e.preventDefault();
                          onPlanningNoteDrop(pid, nidx, dateKey);
                          return;
                        }
                      }
                      const id = e.dataTransfer.getData("application/x-milestone");
                      if (id && onMilestoneDrop) {
                        e.preventDefault();
                        onMilestoneDrop(id, dateKey);
                      }
                    },
                  } : {};
                  const cellBody = (
                    <button
                      onClick={conflict ? undefined : () => onCellClick(dateKey, s.n, primary, projs)}
                      style={{ width: SLOT_CELL_W }}
                      className="shrink-0 border-r border-slate-100 last:border-r-0 cursor-pointer relative outline-none"
                      data-testid={cellTestId}
                      data-conflict={conflict ? "true" : undefined}
                      data-milestone={milestone ? "true" : undefined}
                      aria-label={`${primary.name} — ${primary.client}${conflict ? " (Konflikt)" : ""}${milestone ? ` · Meilenstein: ${milestone.title}` : ""}`}
                      {...dropHandlers}
                    >
                      <span
                        className="absolute inset-0"
                        style={{
                          backgroundColor: primary.planner_dimmed ? "#94a3b8" : s.color,
                          opacity: (primary.planner_dimmed || primary.status === "paused" ? 0.45 : 1) * (isSpecial ? 0.5 : 1),
                        }}
                      />
                      {conflict && (
                        <>
                          <span
                            className="absolute inset-0 pointer-events-none"
                            style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(190,18,60,0.85) 0 2px, transparent 2px 6px)" }}
                          />
                          <span
                            className="absolute inset-0 pointer-events-none"
                            style={{ boxShadow: "inset 0 0 0 2px #be123c" }}
                          />
                        </>
                      )}
                      {milestone && (
                        <span
                          draggable={Boolean(onMilestoneDrop)}
                          onDragStart={(e) => {
                            e.stopPropagation();
                            e.dataTransfer.setData("application/x-milestone", milestoneProjectId);
                            e.dataTransfer.effectAllowed = "move";
                          }}
                          onClick={(e) => e.stopPropagation()}
                          title="Meilenstein ziehen, um Datum zu ändern"
                          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                          style={{
                            width: 12, height: 12, background: "#fff",
                            border: "1.5px solid #0f172a", borderRadius: 2,
                            transform: "translate(-50%, -50%) rotate(45deg)",
                            boxShadow: "0 0 0 1.5px rgba(255,255,255,0.6)",
                            cursor: onMilestoneDrop ? "grab" : "default",
                            zIndex: 3,
                          }}
                          data-testid={`milestone-marker-${milestone.project.id}`}
                        />
                      )}
                      {iconEntries.map((ent, ei) => {
                        // Center planning notes; offset only slightly when
                        // multiple markers (or a main milestone) share the cell.
                        // Main milestone stays centered and visually primary.
                        const noteCount = iconEntries.length;
                        const hasMs = Boolean(milestone);
                        let offsetX = 0;
                        if (noteCount === 1 && hasMs) offsetX = 9;
                        else if (noteCount === 2 && !hasMs) offsetX = ei === 0 ? -6 : 6;
                        else if (noteCount === 2 && hasMs) offsetX = ei === 0 ? -10 : 10;
                        else if (noteCount > 2) {
                          const step = 7;
                          offsetX = -((noteCount - 1) * step) / 2 + ei * step;
                        }
                        return (
                          <span
                            key={`${ent.projectId}-${ent.noteIdx}`}
                            draggable={Boolean(onPlanningNoteDrop)}
                            onDragStart={(e) => {
                              e.stopPropagation();
                              e.dataTransfer.setData("application/x-planning-note", `${ent.projectId}|${ent.noteIdx}`);
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            onClick={(e) => e.stopPropagation()}
                            title={`${ent.title} · ${fmtDateDE(ent.date)}`}
                            className="absolute grid place-items-center"
                            style={{
                              left: "50%", top: "50%",
                              transform: `translate(calc(-50% + ${offsetX}px), -50%)`,
                              width: 14, height: 14, borderRadius: 999,
                              background: "rgba(255,255,255,0.92)",
                              boxShadow: "0 0 0 1px rgba(15,23,42,0.4)",
                              cursor: onPlanningNoteDrop ? "grab" : "default",
                              zIndex: 2,
                            }}
                            data-testid={`vp-planning-icon-${ent.projectId}-${ent.noteIdx}`}
                          >
                            <PlanningIcon icon={ent.icon} size={11} />
                          </span>
                        );
                      })}
                    </button>
                  );
                  if (conflict) {
                    return (
                      <Popover key={s.n}>
                        <PopoverTrigger asChild>{cellBody}</PopoverTrigger>
                        <PopoverContent side="right" className="w-64 p-2" data-testid={`vp-conflict-popover-${dateKey}-${s.n}`}>
                          <div className="text-rose-600 font-semibold mb-1.5 text-xs">
                            ⚠ Konflikt — {projs.length} Projekte
                          </div>
                          {projs.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => onProjectSelect?.(p)}
                              className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-slate-100 transition-colors"
                              data-testid={`vp-conflict-project-${p.id}`}
                            >
                              <div className="font-medium text-slate-900 text-sm">{p.name}</div>
                              <div className="text-xs text-slate-500">{p.client}</div>
                              <div className="text-[11px] text-slate-400 tabular-nums">
                                {fmtDateDE(p.start_date)} → {fmtDateDE(p.end_date)}
                              </div>
                            </button>
                          ))}
                        </PopoverContent>
                      </Popover>
                    );
                  }
                  return (
                    <Tooltip key={s.n} delayDuration={80}>
                      <TooltipTrigger asChild>{cellBody}</TooltipTrigger>
                      <TooltipContent side="right" className="bg-slate-900 text-white text-xs max-w-xs">
                        {milestone && (
                          <div className="text-amber-200 font-semibold mb-1 flex items-baseline gap-1.5">
                            <span aria-hidden>◆</span>
                            <span>{milestone.title} — {milestone.project.name}</span>
                          </div>
                        )}
                        {projs.map((p, i) => (
                          <div key={p.id} className={i > 0 ? "mt-1.5 pt-1.5 border-t border-white/10" : ""}>
                            <div className="font-medium">{p.name}</div>
                            <div className="opacity-80">{p.client}</div>
                            <div className="opacity-60 tabular-nums mt-0.5">{fmtDateDE(p.start_date)} → {fmtDateDE(p.end_date)}</div>
                          </div>
                        ))}
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                return (
                  <button
                    key={s.n}
                    onClick={() => onCellClick(dateKey, s.n, null)}
                    style={{ width: SLOT_CELL_W }}
                    className="shrink-0 border-r border-slate-100 last:border-r-0 hover:bg-slate-50 transition-colors"
                    data-testid={cellTestId}
                    aria-label={`Slot ${s.n} am ${dateKey} frei`}
                    onDragOver={(e) => {
                      if (!onMilestoneDrop && !onPlanningNoteDrop) return;
                      const dt = e.dataTransfer;
                      if (!dt) return;
                      if (dt.types?.includes("application/x-milestone") ||
                          dt.types?.includes("application/x-planning-note")) {
                        e.preventDefault();
                        dt.dropEffect = "move";
                      }
                    }}
                    onDrop={(e) => {
                      const noteId = e.dataTransfer.getData("application/x-planning-note");
                      if (noteId && onPlanningNoteDrop) {
                        const [pid, nidxStr] = noteId.split("|");
                        const nidx = parseInt(nidxStr, 10);
                        if (pid && Number.isFinite(nidx)) {
                          e.preventDefault();
                          onPlanningNoteDrop(pid, nidx, dateKey);
                          return;
                        }
                      }
                      const id = e.dataTransfer.getData("application/x-milestone");
                      if (id && onMilestoneDrop) {
                        e.preventDefault();
                        onMilestoneDrop(id, dateKey);
                      }
                    }}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
});

const WallPlanner = forwardRef(function WallPlanner(
  { startYear, startMonth, projects, specialDays, onCellClick, onMilestoneDrop, onPlanningNoteDrop, onProjectSelect, sortMode = "milestone" },
  ref,
) {
  const months = useMemo(() => rollingMonths(startYear, startMonth), [startYear, startMonth]);
  const occupancy = useMemo(() => buildOccupancy(projects), [projects]);
  const milestones = useMemo(() => buildMilestones(projects), [projects]);
  const specialDaysSet = useMemo(() => buildSpecialDaysMap(specialDays), [specialDays]);
  const planningIcons = useMemo(() => {
    const map = new Map();
    for (const p of projects) {
      if (p.status === "archived") continue;
      if (!Array.isArray(p.planning_notes)) continue;
      p.planning_notes.forEach((n, idx) => {
        if (!n?.show_in_planner || !n?.date) return;
        if (p.start_date && n.date < p.start_date) return;
        if (p.end_date && n.date > p.end_date) return;
        const key = `${n.date}|${p.slot}`;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push({
          projectId: p.id,
          noteIdx: idx,
          icon: n.planner_icon || "production",
          title: n.title || "",
          date: n.date,
        });
      });
    }
    return map;
  }, [projects]);

  // Holidays per visible year (covers up to two distinct years in the rolling window)
  const holidaysByYear = useMemo(() => {
    const years = new Set(months.map((m) => m.year));
    const map = new Map();
    for (const y of years) map.set(y, holidaysForYear(y));
    return map;
  }, [months]);

  const todayKey = useMemo(() => {
    const t = new Date();
    return ymd(t.getFullYear(), t.getMonth(), t.getDate());
  }, []);

  // Sort/filter the visible slot columns.
  //  * "milestone"      → earliest upcoming main milestone OR visible planning note
  //  * "milestone_main" → earliest upcoming main milestone only (notes still render)
  //  * "default"        → natural slot order, but hide slots without any project
  //                       overlapping the 12 rolling months currently rendered
  const sortedSlotList = useMemo(() => {
    if (sortMode === "default") {
      // Compute visible range = months[0]..last day of months[11].
      const first = months[0];
      const last = months[months.length - 1];
      const rangeStart = ymd(first.year, first.month, 1);
      const rangeEndDay = daysInMonth(last.year, last.month);
      const rangeEnd = ymd(last.year, last.month, rangeEndDay);
      const usedSlots = new Set();
      for (const p of projects) {
        if (p.status === "archived") continue;
        if (!p.start_date || !p.end_date) continue;
        // Project overlaps the range if start<=rangeEnd && end>=rangeStart.
        if (p.start_date <= rangeEnd && p.end_date >= rangeStart) {
          usedSlots.add(p.slot);
        }
      }
      return PRIMARY_SLOTS.filter((s) => usedSlots.has(s.n));
    }
    // slot n → { date: ISO, isMain: boolean } (isMain false only in "milestone" mode).
    const earliestPerSlot = new Map();
    const consider = (slotN, date, isMain) => {
      const cur = earliestPerSlot.get(slotN);
      if (!cur || date < cur.date || (date === cur.date && isMain && !cur.isMain)) {
        earliestPerSlot.set(slotN, { date, isMain });
      }
    };
    for (const p of projects) {
      if (p.status === "archived") continue;
      if (p.milestone_date && p.milestone_date >= todayKey) {
        consider(p.slot, p.milestone_date, true);
      }
      if (sortMode === "milestone" && Array.isArray(p.planning_notes)) {
        for (const n of p.planning_notes) {
          if (!n?.show_in_planner || !n?.date) continue;
          if (n.date < todayKey) continue;
          if (p.start_date && n.date < p.start_date) continue;
          if (p.end_date && n.date > p.end_date) continue;
          consider(p.slot, n.date, false);
        }
      }
    }
    return [...PRIMARY_SLOTS].sort((a, b) => {
      const da = earliestPerSlot.get(a.n);
      const db = earliestPerSlot.get(b.n);
      if (da && db) {
        if (da.date !== db.date) return da.date < db.date ? -1 : 1;
        if (da.isMain !== db.isMain) return da.isMain ? -1 : 1;
        return a.n - b.n;
      }
      if (da) return -1;
      if (db) return 1;
      return a.n - b.n;
    });
  }, [projects, todayKey, sortMode, months]);

  // Compute label overlays per rolling month.
  // Rules:
  //  - If project starts inside this month → overlay begins at start_date's day row.
  //  - If project started earlier and continues → overlay begins at day 1 of this month.
  //  - One overlay per (project × month segment). Never per day cell.
  //  - Skipped on months where the project doesn't intersect or has no label.
  const labelOverlaysByMonth = useMemo(() => {
    const out = months.map(() => []);
    for (const p of projects) {
      if (p.status === "archived") continue;
      const label = (p.planner_short_label || "").trim();
      if (!label) continue;
      if (!p.start_date || !p.end_date) continue;
      const slotIdx = sortedSlotList.findIndex((s) => s.n === p.slot);
      if (slotIdx < 0) continue;
      for (let mi = 0; mi < months.length; mi++) {
        const M = months[mi];
        const lastDay = daysInMonth(M.year, M.month);
        const firstOfMonth = ymd(M.year, M.month, 1);
        const lastOfMonth = ymd(M.year, M.month, lastDay);
        if (p.end_date < firstOfMonth) continue;
        if (p.start_date > lastOfMonth) continue;
        const startDay = p.start_date >= firstOfMonth ? parseInt(p.start_date.slice(8, 10), 10) : 1;
        const endDay = p.end_date <= lastOfMonth ? parseInt(p.end_date.slice(8, 10), 10) : lastDay;
        out[mi].push({
          label, startDay, endDay, slotIdx, projectId: p.id,
        });
      }
    }
    return out;
  }, [projects, months, sortedSlotList]);

  const scrollRef = useRef(null);
  const monthRefs = useRef([]);

  // Whenever the rolling-anchor (startYear/startMonth) changes, snap horizontal
  // scroll back to the leftmost month — no smooth-scroll race conditions.
  useEffect(() => {
    const c = scrollRef.current;
    if (c) c.scrollLeft = 0;
  }, [startYear, startMonth]);

  useImperativeHandle(ref, () => ({
    scrollByMonths: (n) => {
      const c = scrollRef.current;
      if (!c) return;
      // find the month closest to current scrollLeft and step from there
      let nearest = 0;
      let best = Infinity;
      monthRefs.current.forEach((el, idx) => {
        if (!el) return;
        const d = Math.abs(el.offsetLeft - c.scrollLeft);
        if (d < best) { best = d; nearest = idx; }
      });
      const target = Math.max(0, Math.min(11, nearest + n));
      const el = monthRefs.current[target];
      if (el) c.scrollTo({ left: Math.max(0, el.offsetLeft - 24), behavior: "smooth" });
    },
    scrollToMonth: (m) => {
      const c = scrollRef.current;
      const el = monthRefs.current[m];
      if (!c || !el) return;
      c.scrollTo({ left: Math.max(0, el.offsetLeft - 24), behavior: "smooth" });
    },
  }));

  return (
    <TooltipProvider>
      <div ref={scrollRef} className="flex-1 h-full overflow-auto bg-slate-50" data-testid="wall-planner">
        <div className="flex flex-row gap-4 p-6 min-w-max">
          {months.map((mObj, idx) => {
            return (
              <MonthColumn
                key={`${mObj.year}-${mObj.month}`}
                ref={(el) => (monthRefs.current[idx] = el)}
                year={mObj.year}
                month={mObj.month}
                slotList={sortedSlotList}
                occupancy={occupancy}
                milestones={milestones}
                holidays={holidaysByYear.get(mObj.year)}
                specialDays={specialDaysSet}
                labelOverlays={labelOverlaysByMonth[idx]}
                planningIcons={planningIcons}
                onCellClick={onCellClick}
                onMilestoneDrop={onMilestoneDrop}
                onPlanningNoteDrop={onPlanningNoteDrop}
                onProjectSelect={onProjectSelect}
                todayKey={todayKey}
              />
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
});

export default WallPlanner;
